import { yupResolver } from '@hookform/resolvers/yup';
import {
  BankDetail,
  Client,
  CreatePaymentRuleDto,
  CreatePaymentRuleDtoServiceTypeEnum,
  ErrorResponse,
  Title,
  UpdatePaymentRuleDto,
  WorkspaceTypeEnum,
} from '@sr-sdks/api-sdk-axios';
import { Button, Dialog, Icon, cn, sonner } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { queryOptionsGetTitlesPaginated } from '../api/titles/titlesApi';
import { queryOptionsGetClientsPaginated } from '../api/workspaces/clientApi';
import { paymentRuleService } from '../reducers/payments/paymentRuleSlice';
import { paymentRuleSchema } from '../schema/paymentRuleSchema';
import { useAppDispatch, useTypedSelector } from '../stateStore';
import { SRBadgeProps, isLoadingState } from '../utils/helpers/common';
import { SuggestionProps } from './AppAutocomplete';
import AutocompleteField from './AutocompleteField';
import { DefaultPaymentRuleProps } from './PaymentRuleForm';
import UnsavedChangesPrompt from './UnsavedChangesPrompt';

const { toast } = sonner;

// Stable references so a still-loading (or absent) query doesn't feed the
// `filteredClients`/`filteredTitles`/`serviceTypes` memos below a fresh `[]`
// on every render (see ReviewClients.tsx's `NO_CLIENTS`).
const NO_CLIENTS: Client[] = [];
const NO_TITLES: Title[] = [];

type BankAccountPaymentRuleFormV3Form = {
  client?: SuggestionProps;
  serviceType?: SuggestionProps;
  title?: SuggestionProps;
  workspaceType?: WorkspaceTypeEnum;
};

type BankAccountPaymentRuleFormV3Props = {
  bankDetail: BankDetail;
  className?: string;
  defaultPaymentRule?: DefaultPaymentRuleProps;
  onCancelClick?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onSuccess?: () => void;
  paymentRuleCount: number;
  paymentRuleId?: string;
  workspaceType?: WorkspaceTypeEnum;
};

const ALL_CLIENTS_OPTION: SuggestionProps = { key: '*', value: 'ALL CLIENTS' };
const ALL_SERVICES_OPTION: SuggestionProps = {
  key: '*',
  value: 'ALL SERVICES',
};
const ALL_TITLES_OPTION: SuggestionProps = { key: '*', value: 'ALL TITLES' };
const DUPLICATE_RULE_MESSAGE_FRAGMENT = 'already exist';

// SDK types `ErrorResponse.message` as `object`, but at runtime the backend
// sends `string | string[]` (see `ErrorResponse` entity). Narrow defensively.
const extractErrorMessage = (payload: unknown): string | undefined => {
  const message = (payload as ErrorResponse | undefined)?.message as unknown;

  if (typeof message === 'string') return message;
  if (Array.isArray(message)) {
    return message.filter((m): m is string => typeof m === 'string').join(', ');
  }

  return undefined;
};

const isDuplicateRuleError = (payload: unknown) => {
  const error = payload as ErrorResponse | undefined;

  if (!error || error.statusCode !== 400) return false;

  return (
    extractErrorMessage(payload)
      ?.toLowerCase()
      .includes(DUPLICATE_RULE_MESSAGE_FRAGMENT) ?? false
  );
};

const errorToString = (payload: unknown) =>
  extractErrorMessage(payload) ?? 'Something went wrong. Please try again.';

export const BankAccountPaymentRuleFormV3 = ({
  bankDetail,
  className,
  defaultPaymentRule,
  onCancelClick,
  onDirtyChange,
  onSuccess,
  paymentRuleCount,
  paymentRuleId,
  workspaceType,
}: BankAccountPaymentRuleFormV3Props) => {
  const dispatch = useAppDispatch();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [
    showDuplicatePaymentRuleErrorModal,
    setShowDuplicatePaymentRuleErrorModal,
  ] = useState(false);

  const isCreating = useTypedSelector(paymentRuleService.selectors.isCreating);
  const isUpdating = useTypedSelector(paymentRuleService.selectors.isUpdating);

  // Fetch clients and titles
  const { data: clientsData } = useQuery(
    queryOptionsGetClientsPaginated({
      page: 1,
      pageSize: 100,
      sortBy: 'created:asc',
      workspaceId: bankDetail.workspaceId,
    }),
  );
  const clients = clientsData?.items ?? NO_CLIENTS;

  const { data: titlesData } = useQuery(
    queryOptionsGetTitlesPaginated({
      page: 1,
      pageSize: 100,
      sortBy: 'created:asc',
      workspaceId: bankDetail.workspaceId,
    }),
  );
  const titles = titlesData?.items ?? NO_TITLES;

  const effectiveWorkspaceType =
    workspaceType ?? WorkspaceTypeEnum.BusinessAgency;
  const isAgentWorkspace =
    effectiveWorkspaceType === WorkspaceTypeEnum.BusinessAgency;

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    resetField,
  } = useForm<BankAccountPaymentRuleFormV3Form>({
    defaultValues: {
      workspaceType: effectiveWorkspaceType,
    },
    mode: 'all',
    resolver: yupResolver(paymentRuleSchema),
  });

  const watchedClient = useWatch({ control, name: 'client' });
  const watchedTitle = useWatch({ control, name: 'title' });
  const watchedServiceType = useWatch({ control, name: 'serviceType' });

  const filteredClients = useMemo(() => {
    const selectedTitle = titles.find((t) => t._id === watchedTitle?.key);

    if (!selectedTitle) return clients;

    return clients.filter((client) =>
      selectedTitle.ownerClientIds?.includes(client._id),
    );
  }, [clients, titles, watchedTitle?.key]);

  const filteredTitles = useMemo(() => {
    if (!isAgentWorkspace) return titles;
    if (!watchedClient?.key || watchedClient.key === '*') return titles;

    return titles.filter((title) =>
      title.ownerClientIds?.includes(watchedClient.key),
    );
  }, [isAgentWorkspace, titles, watchedClient?.key]);

  const serviceTypes = useMemo<SuggestionProps[]>(() => {
    const matchingTitles =
      watchedTitle?.key === '*'
        ? titles
        : titles.filter((title) => title._id === watchedTitle?.key);
    const uniqueServiceTypes = [
      ...new Set(matchingTitles.flatMap((title) => title.serviceType)),
    ];

    return [
      ALL_SERVICES_OPTION,
      ...uniqueServiceTypes.map((value) => ({
        key: value,
        value: SRBadgeProps[value].label.toUpperCase(),
      })),
    ];
  }, [titles, watchedTitle?.key]);

  const isUpdatingExistingRule = Boolean(defaultPaymentRule && paymentRuleId);
  const isClientUnchanged =
    !isAgentWorkspace ||
    watchedClient?.value === defaultPaymentRule?.client?.value;
  const isUnchangedFromDefault =
    isUpdatingExistingRule &&
    watchedServiceType?.value === defaultPaymentRule?.serviceType?.value &&
    isClientUnchanged &&
    watchedTitle?.value === defaultPaymentRule?.title?.value;
  const isSubmitting = isLoadingState(isCreating) || isLoadingState(isUpdating);

  const handleSubmitForm = async (
    formData: BankAccountPaymentRuleFormV3Form,
  ) => {
    if (
      (isAgentWorkspace && !formData.client?.key) ||
      !formData.serviceType?.key ||
      !formData.title?.key
    ) {
      return;
    }

    if (
      isAgentWorkspace &&
      formData.serviceType.key === '*' &&
      formData.title.key === '*' &&
      formData.client?.key === '*'
    ) {
      return setShowErrorModal(true);
    }

    if (
      !isAgentWorkspace &&
      formData.serviceType.key === '*' &&
      formData.title.key === '*'
    ) {
      return setShowErrorModal(true);
    }

    const payload: CreatePaymentRuleDto = {
      airwallexBeneficiaryId: bankDetail.airwallexBeneficiaryId,
      bankDetailsId: bankDetail._id,
      clientId: isAgentWorkspace ? (formData.client?.key ?? '*') : '*',
      serviceType: formData.serviceType
        .key as CreatePaymentRuleDtoServiceTypeEnum,
      titleId: formData.title.key,
      workspaceId: bankDetail.workspaceId,
    };

    if (isUpdatingExistingRule && paymentRuleId) {
      const paymentRulePayload: UpdatePaymentRuleDto = payload;
      const updateResponse = await dispatch(
        paymentRuleService.update({
          paymentRuleId,
          paymentRulePayload,
        }),
      );

      if (paymentRuleService.update.fulfilled.match(updateResponse)) {
        onSuccess?.();

        return;
      }

      if (isDuplicateRuleError(updateResponse.payload)) {
        setShowDuplicatePaymentRuleErrorModal(true);
      } else {
        toast.error(errorToString(updateResponse.payload));
      }

      return;
    }

    const createResponse = await dispatch(paymentRuleService.create(payload));

    if (paymentRuleService.create.fulfilled.match(createResponse)) {
      onSuccess?.();

      return;
    }

    if (isDuplicateRuleError(createResponse.payload)) {
      setShowDuplicatePaymentRuleErrorModal(true);
    } else {
      toast.error(errorToString(createResponse.payload));
    }
  };

  const checkKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  useEffect(() => {
    reset(
      defaultPaymentRule
        ? {
            client: defaultPaymentRule.client,
            serviceType: defaultPaymentRule.serviceType,
            title: defaultPaymentRule.title,
            workspaceType: effectiveWorkspaceType,
          }
        : {
            workspaceType: effectiveWorkspaceType,
          },
    );
  }, [defaultPaymentRule, effectiveWorkspaceType, reset]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const clientSuggestions = useMemo(
    () => [
      ALL_CLIENTS_OPTION,
      ...filteredClients.map((client) => ({
        key: client._id,
        value: client.clientName.toUpperCase(),
      })),
    ],
    [filteredClients],
  );

  const titleSuggestions = useMemo(
    () => [
      ALL_TITLES_OPTION,
      ...filteredTitles.map((title) => ({
        key: title._id,
        value: title.title.toUpperCase(),
      })),
    ],
    [filteredTitles],
  );

  return (
    <div
      className={cn(
        'flex w-full flex-col justify-center gap-2.5 self-stretch pt-2.5',
        className,
      )}
    >
      <UnsavedChangesPrompt hasUnsavedChanges={isDirty} />

      <form
        className="flex w-full flex-col items-start justify-center gap-3 sm:relative sm:flex-[1_0_0] sm:gap-2 sm:pr-20"
        onKeyDown={checkKeyDown}
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <div className="grid w-full grid-cols-1 items-start gap-2 self-stretch sm:grid-cols-[3.5rem_7rem_minmax(0,18rem)] sm:items-center sm:gap-x-3 lg:grid-cols-[3.5rem_7rem_minmax(0,28rem)]">
          <span className="text-sm font-bold text-neutral-950">
            Rule {paymentRuleCount}
          </span>
          {isAgentWorkspace && (
            <>
              <span className="whitespace-nowrap text-sm font-normal text-neutral-700">
                When Client is
              </span>
              <AutocompleteField
                name="client"
                control={control}
                error={errors.client?.value?.message}
                suggestions={clientSuggestions}
                onSelect={() => {
                  resetField('title');
                  resetField('serviceType');
                }}
              />
            </>
          )}
          <span className="whitespace-nowrap text-sm font-normal text-neutral-700 sm:col-start-2">
            {isAgentWorkspace ? 'And' : 'When'} Title is
          </span>
          <AutocompleteField
            name="title"
            control={control}
            error={errors.title?.value?.message || errors.title?.message}
            suggestions={titleSuggestions}
            onSelect={() => {
              resetField('serviceType');
            }}
          />
          <span className="whitespace-nowrap text-sm font-normal text-neutral-700 sm:col-start-2">
            And Service is
          </span>
          <AutocompleteField
            name="serviceType"
            control={control}
            error={errors.serviceType?.value?.message}
            suggestions={serviceTypes}
          />
        </div>

        <div className="flex w-full items-center justify-end gap-1.5 sm:absolute sm:right-0 sm:top-0 sm:w-auto">
          <Button
            className="h-8 w-8"
            disabled={isSubmitting || !isDirty || isUnchangedFromDefault}
            loading={isSubmitting}
            size="icon"
            type="submit"
            variant="outline"
          >
            {!isSubmitting && (
              <>
                <span className="sr-only">Confirm Payment Rule</span>
                <Icon icon="check" />
              </>
            )}
          </Button>
          <Button
            className="h-8 w-8"
            size="icon"
            type="button"
            variant="outline"
            onClick={() => onCancelClick?.()}
          >
            <span className="sr-only">Cancel Payment Rule</span>
            <Icon icon="x" />
          </Button>
        </div>
      </form>

      <Dialog
        heading="Invalid Payment Rule"
        actionButtonProps={{
          children: 'Close',
          onClick: () => setShowErrorModal(false),
        }}
        hasCancelButton={false}
        open={showErrorModal}
        onOpenChange={() => setShowErrorModal(false)}
      >
        <div className="font-sans text-base leading-normal">
          This payment rule has the same effect as setting a default bank
          account. Select &apos;Set as Default&apos; on the bank account&apos;s
          actions menu.
        </div>
      </Dialog>

      <Dialog
        heading="Payment Rule Already Exists"
        actionButtonProps={{
          children: 'Close',
          onClick: () => setShowDuplicatePaymentRuleErrorModal(false),
        }}
        hasCancelButton={false}
        open={showDuplicatePaymentRuleErrorModal}
        onOpenChange={() => setShowDuplicatePaymentRuleErrorModal(false)}
      >
        <div className="font-sans text-base leading-normal">
          A payment rule with this criteria already exists on your workspace.
        </div>
      </Dialog>
    </div>
  );
};
